import React from "react";
import { ExternalLink, Link2Off } from "lucide-react";
import { fmtMoney } from "@/components/ripeness/radarMeta";

export const fmtRange = (r, cur) => (r ? `${fmtMoney(r.low, cur)}–${fmtMoney(r.high, cur)}` : "—");

/** Row cell: bold expected midpoint, awarded range beneath, phase valued. */
export function ValueCell({ track }) {
    const v = track.value;
    const id = track.track_id;
    if (!v) return null;
    if (!v.expected) {
        return (
            <div className="hidden md:block w-32 text-right" data-testid={`value-cell-${id}`} title={v.reason_label || "No value"}>
                <div className="text-sm font-semibold text-slate-500" data-testid={`value-expected-${id}`}>—</div>
                <div className="text-[10px] text-slate-600 leading-tight" data-testid={`value-reason-${id}`}>{v.reason_label || "no value"}</div>
            </div>
        );
    }
    const w = track.value_weighted || v.weighted;
    const weighted = w && w.weight !== 1;
    const bpp = v.factors?.budget_per_patient;
    const src = bpp?.locked ? "locked" : bpp?.n ? `${bpp.source} · ${bpp.n} CTAs` : null;
    return (
        <div className="hidden md:block w-32 text-right" data-testid={`value-cell-${id}`}
            title={`Expected ${fmtRange(v.expected, v.currency)} · if awarded ${fmtRange(v.if_awarded, v.currency)} (P${v.phase})${weighted ? ` · value to us ×${w.weight}` : ""}${src ? ` · budget ${src}` : ""}`}>
            <div className="text-base font-bold text-emerald-200 leading-tight" data-testid={`value-expected-${id}`}>{fmtMoney(weighted ? w.mid : v.expected.mid, v.currency)}</div>
            {weighted ? <div className="text-[10px] text-violet-300 leading-tight" data-testid={`value-weight-${id}`}>×{Number(w.weight).toFixed(2)} to us · raw {fmtMoney(v.expected.mid, v.currency)}</div>
                : <div className="text-[10px] text-slate-400 leading-tight" data-testid={`value-range-${id}`}>awarded {fmtRange(v.if_awarded, v.currency)}</div>}
            <div className="text-[10px] text-slate-600 uppercase tracking-wider">P{v.phase} · {weighted ? "value to us" : "expected"}{src ? <span className="text-violet-400/80 normal-case tracking-normal" data-testid={`value-src-${id}`}> · {bpp.locked ? "locked" : "CTA data"}</span> : null}</div>
        </div>
    );
}

/** Newest source article behind a track (opens in a new tab). */
export function SourceLink({ track, className = "" }) {
    const id = track.track_id;
    const n = track.sources?.length || 0;
    if (!track.source_url) {
        return (
            <span className={`inline-flex items-center justify-center w-7 h-7 text-slate-700 ${className}`} title="No article URL on record"
                data-testid={`source-none-${id}`}>
                <Link2Off className="w-3.5 h-3.5" />
            </span>
        );
    }
    return (
        <a href={track.source_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
            className={`inline-flex items-center justify-center gap-0.5 w-9 h-7 rounded border border-sky-400/30 text-sky-300 hover:bg-sky-400/10 ${className}`}
            title={`Source article${n > 1 ? ` (+${n - 1} more in the drill-down)` : ""}: ${track.source_url}`}
            data-testid={`source-link-${id}`}>
            <ExternalLink className="w-3.5 h-3.5" />
            {n > 1 ? <span className="text-[10px]">{n}</span> : null}
        </a>
    );
}
